import * as React from "react";

import { BaseIcon } from "../Icons/BaseIcon";
import clsxm from "../../lib/clsxm";

export enum BadgeType {
  Success = "success",
  Custom = "custom",
  Warning = "warning",
  Error = "error",
  Neutral = "neutral",
}

export enum BadgeStyle {
  Regular = "regular",
  Border = "border",
}

interface BadgeProps {
  size: "50" | "100" | "150" | "200";
  children: React.ReactNode;
  style?: BadgeStyle;
  icon?: React.ReactNode | null;
  onClick?: (event: React.MouseEvent) => void;
  className?: string;
}

interface BadgePresetProps extends BadgeProps {
  type:
    | BadgeType.Success
    | BadgeType.Warning
    | BadgeType.Error
    | BadgeType.Neutral;
}

interface BadgeCustomProps extends BadgeProps {
  type: BadgeType.Custom;
  className: string;
}

const Badge = ({
  icon = null,
  style = BadgeStyle.Regular,
  size,
  type,
  className,
  onClick,
  children,
}: BadgePresetProps | BadgeCustomProps) => {
  let classNames: string[] = [];
  let iconClassNames: string[] = [];
  let backgroundClassName = "";
  let borderClassName = "";

  switch (size) {
    case "50": {
      classNames = ["p-0", "px-0.5 rounded-lg text-xsb gap-0.5"];
      borderClassName = "shadow-innerBorder1";
      iconClassNames = ["text-base"];
      break;
    }
    case "100":
      classNames = [
        icon ? "pl-1.5 pr-2" : "px-1.5",
        "py-0.5 rounded-lg text-xsb gap-0.5",
      ];
      borderClassName = "shadow-innerBorder1";
      iconClassNames = ["text-base"];
      break;
    case "150":
      classNames = [
        icon ? "px-2" : "px-2",
        "py-2t rounded-lg text-smb gap-0.5",
      ];
      borderClassName = "shadow-innerBorder1";
      iconClassNames = ["text-base"];
      break;
    case "200":
      classNames = ["px-3 py-1 rounded-xl text-mdb gap-1"];
      borderClassName = "shadow-innerBorder2";
      iconClassNames = ["text-2xl"];
      break;
    default:
      throw new Error("Unsupported size");
  }

  switch (type) {
    case BadgeType.Success:
      classNames = [...classNames, "text-green-800"];
      backgroundClassName = "bg-green-50a";
      break;
    case BadgeType.Warning:
      classNames = [...classNames, "text-yellow-800"];
      backgroundClassName = "bg-yellow-50a";
      break;
    case BadgeType.Error:
      classNames = [...classNames, "text-red-800"];
      backgroundClassName = "bg-red-50a";
      break;
    case BadgeType.Neutral:
      classNames = [...classNames, "text-black-900 shadow-black-150 mt-1.5"];
      backgroundClassName = "bg-gray-100";
      break;
    case BadgeType.Custom:
      // do nothing; styles from className will be applied
      break;
    default:
      throw new Error("Unsupported type");
  }

  switch (style) {
    case BadgeStyle.Regular:
      borderClassName = "";
      break;
    case BadgeStyle.Border:
      backgroundClassName = "";
      break;
  }

  return (
    <div
      className={
        // hack for clsxm
        borderClassName +
        " " +
        clsxm(
          "inline-flex items-center border-current backdrop-blur-2xl",
          classNames,
          backgroundClassName,
          className ?? ""
        )
      }
      onClick={onClick}
    >
      {icon ? (
        <BaseIcon
          icon={icon}
          className={clsxm(iconClassNames, "leading-none")}
        />
      ) : null}
      {children}
    </div>
  );
};

export default Badge;
